import gql from 'graphql-tag';
import apolloClient from '../apollo';

/**
 * State
 */
const defaultOrder = () => {
  return {
    _id: '_new',
    number: '_new',
    supplier: null, // Will be required, but initialized as null for form handling
    items: [],
    deliveryLocation: {
      storage: null,
      project: null,
    },
    orderState: 'DRAFT',
    orderType: 'PURCHASE',
    priority: 'NORMAL',
    submittedToSupplierDate: null,
    expectedDeliveryDate: null,
    actualDeliveryDate: null,
    totalCost: null,
    notes: '',
    project: null,
    createdBy: null,
    approvedBy: null,
  };
};

const getDefaultState = () => {
  return {
    orders: [],
    orderCount: 0,
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for an order list view
 * used for paginated order queries
 * @type {DocumentNode}
 */
const ORDER_LIST_REQUEST_FIELDS = gql`
  fragment orderListFields on Order {
    _id
    number
    supplier {
      _id
      number
      name
    }
    supplierOrderNumber
    submittedToSupplierDate
    expectedDeliveryDate
    actualDeliveryDate
    orderState
    orderType
    priority
    totalCost
    project {
      id
      number
      customer {
        firstname
        lastname
      }
    }
    createdBy {
      _id
      firstname
      lastname
      email
    }
    updatedBy {
      _id
      firstname
      lastname
      email
    }
  }
`;

/** Order list of the view orders. */
const ORDER_LIST_QUERY = gql`
  query ordersPaginated(
    $pagination: PaginationInput
    $sorting: SortingInput
    $filters: OrderFilterInput
  ) {
    ordersPaginated(pagination: $pagination, sorting: $sorting, filters: $filters) {
      orders {
        ...orderListFields
      }
      orderCount
    }
  }
  ${ORDER_LIST_REQUEST_FIELDS}
`;

/** Order list of the block project-orders; the server restricts it to the project. */
const PROJECT_ORDER_LIST_QUERY = gql`
  query projectOrders(
    $projectNumber: Int!
    $pagination: PaginationInput
    $sorting: SortingInput
    $filters: OrderFilterInput
  ) {
    projectByNumber(projectNumber: $projectNumber) {
      id
      number
      blocks {
        orders {
          id
          number
          orders(pagination: $pagination, sorting: $sorting, filters: $filters) {
            orders {
              ...orderListFields
            }
            orderCount
          }
        }
      }
    }
  }
  ${ORDER_LIST_REQUEST_FIELDS}
`;

/**
 * Request of the order list: with a project number the block project-orders, without it the
 * global query ordersPaginated. `pagesOf` returns the `OrderPages` of the response.
 */
const orderListRequest = (projectNumber) => {
  if (projectNumber) {
    return {
      query: PROJECT_ORDER_LIST_QUERY,
      pagesOf: (data) => data.projectByNumber.blocks.orders.orders,
    };
  }
  return { query: ORDER_LIST_QUERY, pagesOf: (data) => data.ordersPaginated };
};

/**
 * Defines the fields which are requested for an order
 * used for all queries/mutations which return an order
 * @type {DocumentNode}
 */
const ORDER_REQUEST_FIELDS = gql`
  fragment orderFields on Order {
    _id
    number
    supplier {
      _id
      number
      name
    }
    supplierOrderNumber
    submittedToSupplierDate
    expectedDeliveryDate
    actualDeliveryDate
    orderState
    orderType
    priority
    totalCost
    notes
    orderExportAttachments {
      id
      path
      filename
      displayFilename
      size
      mimetype
    }
    orderExportBuildState
    project {
      id
      number
      customer {
        firstname
        lastname
        street
        streetNumber
        streetNumberSuffix
        city
        zip
      }
    }
    items {
      quantityOrdered
      quantityReceived
      unitPrice
      item {
        _id
        number
        description
        unit
        price
        priceCalculation {
          # Item pricing
          itemMarkup
          itemProfitMarkup
          itemCustomerPrice

          # Labor pricing
          laborCost
          laborMarkup
          laborProfitMarkup
          laborCustomerPrice

          # Total pricing
          totalCustomerPrice

          calculatedAt
        }
      }
    }
    deliveryLocation {
      storage {
        _id
        name
      }
      project {
        id
        number
        customer {
          firstname
          lastname
        }
      }
    }
    createdBy {
      _id
      firstname
      lastname
      email
    }
    approvedBy {
      _id
      firstname
      lastname
      email
    }
    updatedBy {
      _id
      firstname
      lastname
      email
    }
    createdAt
    updatedAt
  }
`;

/** One order of the view orders. */
const ORDER_BY_NUMBER_QUERY = gql`
  query orderByNumber($orderNumber: Int!) {
    orderByNumber(orderNumber: $orderNumber) {
      ...orderFields
    }
  }
  ${ORDER_REQUEST_FIELDS}
`;

/** One order of the block project-orders; an order of another project is not found. */
const PROJECT_ORDER_QUERY = gql`
  query projectOrder($projectNumber: Int!, $orderNumber: Int!) {
    projectByNumber(projectNumber: $projectNumber) {
      id
      number
      blocks {
        orders {
          id
          number
          order(orderNumber: $orderNumber) {
            ...orderFields
          }
        }
      }
    }
  }
  ${ORDER_REQUEST_FIELDS}
`;

/**
 * Request of one order: with a project number the block project-orders, without it the global
 * query orderByNumber. `orderOf` returns the `Order` of the response.
 */
const orderRequest = (orderNumber, projectNumber) => {
  if (projectNumber) {
    return {
      query: PROJECT_ORDER_QUERY,
      variables: { projectNumber, orderNumber },
      orderOf: (data) => data.projectByNumber.blocks.orders.order,
    };
  }
  return {
    query: ORDER_BY_NUMBER_QUERY,
    variables: { orderNumber },
    orderOf: (data) => data.orderByNumber,
  };
};

/**
 * sanitize inputs and create payload to send to the graphql api
 * @param {*} order
 */
const createPayload = (order) => {
  const payload = {
    supplierOrderNumber: order.supplierOrderNumber,
    orderState: order.orderState,
    orderType: order.orderType,
    items: order.items.map((item) => ({
      item: item.item._id || item.item,
      quantityOrdered: item.quantityOrdered,
      quantityReceived: item.quantityReceived || 0,
      unitPrice: item.unitPrice || null,
    })),
    deliveryLocation: {
      storage: order.deliveryLocation.storage?._id || order.deliveryLocation.storage,
      project: order.deliveryLocation.project?._id || order.deliveryLocation.project,
    },
  };

  // Add supplier ID if available
  if (order.supplier) {
    payload.supplier = order.supplier._id;
  }

  // Add optional fields only if they have values
  if (order.project) {
    payload.project = order.project.id || null;
  }
  if (order.expectedDeliveryDate) {
    payload.expectedDeliveryDate = order.expectedDeliveryDate;
  }
  if (order.actualDeliveryDate) {
    payload.actualDeliveryDate = order.actualDeliveryDate;
  }
  if (order.priority) {
    payload.priority = order.priority;
  }
  if (order.totalCost !== null && order.totalCost !== undefined) {
    payload.totalCost = order.totalCost;
  }
  if (order.notes) {
    payload.notes = order.notes;
  }
  if (order.approvedBy) {
    payload.approvedBy = order.approvedBy._id || order.approvedBy;
  }
  if (order.submittedToSupplierDate) {
    payload.submittedToSupplierDate = order.submittedToSupplierDate;
  }

  console.log(payload);

  return payload;
};

/**
 * Payload of the block project-orders (`ProjectOrderInput`): without project, the server sets it.
 * @param {*} order
 */
const createProjectOrderPayload = (order) => {
  const payload = createPayload(order);
  delete payload.project;
  return payload;
};

/**
 * Request of a create: with a project number the mutation of the block project-orders (payload
 * without project), without it the global mutation.
 */
const createOrderRequest = (order, projectNumber) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation createProjectOrder($projectNumber: Int!, $payload: ProjectOrderInput!) {
          createProjectOrder(projectNumber: $projectNumber, orderInput: $payload) {
            ...orderFields
          }
        }
        ${ORDER_REQUEST_FIELDS}
      `,
      variables: { projectNumber, payload: createProjectOrderPayload(order) },
      resultField: 'createProjectOrder',
    };
  }
  return {
    mutation: gql`
      mutation createOrder($payload: OrderInput!) {
        createOrder(orderInput: $payload) {
          ...orderFields
        }
      }
      ${ORDER_REQUEST_FIELDS}
    `,
    variables: { payload: createPayload(order) },
    resultField: 'createOrder',
  };
};

/** Request of an update, chosen by the project number as `createOrderRequest`. */
const updateOrderRequest = (order, projectNumber) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation updateProjectOrder(
          $projectNumber: Int!
          $orderNumber: Int!
          $payload: ProjectOrderInput!
        ) {
          updateProjectOrder(
            projectNumber: $projectNumber
            orderNumber: $orderNumber
            orderInput: $payload
          ) {
            ...orderFields
          }
        }
        ${ORDER_REQUEST_FIELDS}
      `,
      variables: {
        projectNumber,
        orderNumber: order.number,
        payload: createProjectOrderPayload(order),
      },
      resultField: 'updateProjectOrder',
    };
  }
  return {
    mutation: gql`
      mutation updateOrder($orderNumber: Int!, $payload: OrderInput!) {
        updateOrder(orderNumber: $orderNumber, orderInput: $payload) {
          ...orderFields
        }
      }
      ${ORDER_REQUEST_FIELDS}
    `,
    variables: { orderNumber: order.number, payload: createPayload(order) },
    resultField: 'updateOrder',
  };
};

/** Request of a delete, chosen by the project number as `createOrderRequest`. */
const deleteOrderRequest = (orderNumber, projectNumber) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation deleteProjectOrder($projectNumber: Int!, $orderNumber: Int!) {
          deleteProjectOrder(projectNumber: $projectNumber, orderNumber: $orderNumber)
        }
      `,
      variables: { projectNumber, orderNumber },
    };
  }
  return {
    mutation: gql`
      mutation deleteOrder($orderNumber: Int!) {
        deleteOrder(orderNumber: $orderNumber)
      }
    `,
    variables: { orderNumber },
  };
};

/** Request of a single export, chosen by the project number as `createOrderRequest`. */
const buildOrderExportRequest = (orderNumber, projectNumber) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation buildProjectOrderExport($projectNumber: Int!, $orderNumber: Int!) {
          buildProjectOrderExport(projectNumber: $projectNumber, orderNumber: $orderNumber)
        }
      `,
      variables: { projectNumber, orderNumber },
      resultField: 'buildProjectOrderExport',
    };
  }
  return {
    mutation: gql`
      mutation buildSingleOrderExport($orderNumber: Int!) {
        buildSingleOrderExport(orderNumber: $orderNumber)
      }
    `,
    variables: { orderNumber },
    resultField: 'buildSingleOrderExport',
  };
};

/**
 * Request of an attachment delete: with a project number the mutation of the block
 * project-orders (the order by number), without it the global mutation (the order by id).
 */
const deleteOrderAttachmentRequest = ({
  orderId,
  orderNumber,
  attachmentId,
  attachmentType,
  projectNumber,
}) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation deleteProjectOrderAttachment(
          $projectNumber: Int!
          $orderNumber: Int!
          $attachmentId: ID!
          $attachmentType: String!
        ) {
          deleteProjectOrderAttachment(
            projectNumber: $projectNumber
            orderNumber: $orderNumber
            attachmentId: $attachmentId
            attachmentType: $attachmentType
          ) {
            ...orderFields
          }
        }
        ${ORDER_REQUEST_FIELDS}
      `,
      variables: { projectNumber, orderNumber, attachmentId, attachmentType },
      resultField: 'deleteProjectOrderAttachment',
    };
  }
  return {
    mutation: gql`
      mutation deleteOrderAttachment($orderId: ID!, $attachmentId: ID!, $attachmentType: String!) {
        deleteOrderAttachment(
          orderId: $orderId
          attachmentId: $attachmentId
          attachmentType: $attachmentType
        ) {
          ...orderFields
        }
      }
      ${ORDER_REQUEST_FIELDS}
    `,
    variables: { orderId, attachmentId, attachmentType },
    resultField: 'deleteOrderAttachment',
  };
};

/** Request of the export build state of one order, chosen by the project number. */
const orderBuildStateRequest = (orderNumber, projectNumber) => {
  if (projectNumber) {
    return {
      query: gql`
        query projectOrderExportAttachmentBuildState($projectNumber: Int!, $orderNumber: Int!) {
          projectByNumber(projectNumber: $projectNumber) {
            blocks {
              orders {
                order(orderNumber: $orderNumber) {
                  _id
                  number
                  orderExportBuildState
                  orderExportAttachments {
                    id
                    path
                    filename
                    displayFilename
                    size
                    mimetype
                  }
                }
              }
            }
          }
        }
      `,
      variables: { projectNumber, orderNumber },
      orderOf: (data) => data.projectByNumber.blocks.orders.order,
    };
  }
  return {
    query: gql`
      query orderByNumber($orderNumber: Int!) {
        orderByNumber(orderNumber: $orderNumber) {
          _id
          number
          orderExportBuildState
          orderExportAttachments {
            id
            path
            filename
            displayFilename
            size
            mimetype
          }
        }
      }
    `,
    variables: { orderNumber },
    orderOf: (data) => data.orderByNumber,
  };
};

/**
 * Actions
 */
const actions = {
  /**
   * Fetch a paginated, sorted and filtered order list
   * @param {*} param0
   * @param {*} payload
   */
  async fetchOrdersPaginated(
    { commit, dispatch },
    { pagination, sorting, filters, projectNumber }
  ) {
    const { query, pagesOf } = orderListRequest(projectNumber);
    try {
      const response = await apolloClient.query({
        query,
        variables: {
          ...(projectNumber ? { projectNumber } : {}),
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });
      const orderPages = pagesOf(response.data);
      commit('SET_ORDERS', orderPages.orders);
      commit('SET_ORDER_COUNT', orderPages.orderCount);
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },

  /** Loads one order; with `projectNumber` through the block project-orders. */
  async fetchOrderByNumber({ commit, dispatch }, { orderNumber, projectNumber }) {
    try {
      const { query, variables, orderOf } = orderRequest(parseInt(orderNumber), projectNumber);
      const response = await apolloClient.query({ query, variables });
      commit('ADD_OR_UPDATE_ORDER', orderOf(response.data));
    } catch (err) {
      throw new Error(err);
    }
  },

  /** Creates the order; with `projectNumber` through the block project-orders. */
  async createOrder({ commit, dispatch }, { order, projectNumber }) {
    const { mutation, variables, resultField } = createOrderRequest(order, projectNumber);

    try {
      const response = await apolloClient.mutate({ mutation, variables });
      dispatch('message', {
        message: 'Material gespeichert!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_ORDER', '_new'); // remove temporary _new order
      commit('ADD_OR_UPDATE_ORDER', response.data[resultField]);
      return response.data[resultField].number;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /** Updates the order; with `projectNumber` through the block project-orders. */
  async updateOrder({ commit, dispatch }, { order, projectNumber }) {
    const { mutation, variables, resultField } = updateOrderRequest(order, projectNumber);

    try {
      const response = await apolloClient.mutate({ mutation, variables });
      dispatch('message', {
        message: 'Material aktualisiert!',
        type: 'success',
        lifetime: 2,
      });
      commit('ADD_OR_UPDATE_ORDER', response.data[resultField]);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim bearbeiten! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /** Deletes the order; with `projectNumber` through the block project-orders. */
  async deleteOrder({ commit, dispatch }, { orderNumber, projectNumber }) {
    try {
      await apolloClient.mutate(deleteOrderRequest(orderNumber, projectNumber));
      dispatch('message', {
        message: 'Material gelöscht!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_ORDER', orderNumber);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * create a new order object in store and return it
   * @param {*} param0
   * @param {*} order
   */
  async initOrder({ commit }) {
    commit('ADD_OR_UPDATE_ORDER', defaultOrder());
  },

  orderLocalUpdate({ commit }, order) {
    commit('ADD_OR_UPDATE_ORDER', order);
  },

  orderCleanup({ commit }) {
    commit('REMOVE_ORDER', '_new');
  },

  /**
   * Request an async build of a single order export
   * @param {*} context
   * @param {*} payload - contains orderNumber and, in the block, projectNumber
   */
  async buildSingleOrderExport({ commit, dispatch }, { orderNumber, projectNumber }) {
    if (!orderNumber) {
      throw new Error('OrderNumber is undefined or null');
    }

    const { mutation, variables, resultField } = buildOrderExportRequest(
      orderNumber,
      projectNumber
    );
    try {
      const response = await apolloClient.mutate({ mutation, variables });
      commit('UPDATE_ORDER_EXPORT_BUILD_STATE', {
        orderExportBuildState: response.data[resultField],
        orderNumber: orderNumber,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Delete an attachment from an order
   * @param {*} param0
   * @param {*} payload - contains orderId, attachmentId, attachmentType; in the block also
   * orderNumber and projectNumber
   */
  async deleteOrderAttachment({ commit, dispatch }, payload) {
    const { mutation, variables, resultField } = deleteOrderAttachmentRequest(payload);
    try {
      const response = await apolloClient.mutate({ mutation, variables });
      commit('ADD_OR_UPDATE_ORDER', response.data[resultField]);
      dispatch('message', {
        message: 'Anhang gelöscht!',
        type: 'success',
        lifetime: 2,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Fetch the build state and attachments for order export
   * @param {*} param0
   * @param {*} payload - contains orderNumber and, in the block, projectNumber
   */
  async fetchOrderExportAttachmentBuildState({ commit, dispatch }, { orderNumber, projectNumber }) {
    try {
      const { query, variables, orderOf } = orderBuildStateRequest(
        parseInt(orderNumber),
        projectNumber
      );
      const response = await apolloClient.query({ query, variables });
      commit('ADD_OR_UPDATE_ORDER', orderOf(response.data));
    } catch (err) {
      throw new Error(err);
    }
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_ORDER_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_ORDERS: (state, orders) => (orders ? (state.orders = orders) : (state.orders = [])),
  ADD_ORDER: (state, order) => state.orders.push(order),
  ADD_OR_UPDATE_ORDER: (state, updatedOrder) => {
    const existingOrder = state.orders.find((order) => order._id === updatedOrder._id);
    if (existingOrder) {
      Object.assign(existingOrder, updatedOrder);
    } else {
      state.orders.push(updatedOrder);
    }
  },
  REMOVE_ORDER: (state, orderId) => {
    const index = state.orders.findIndex((order) => order._id === orderId);
    state.orders.splice(index, 1);
  },
  UPDATE_ORDER: (state, updatedOrder) => {
    const order = state.orders.find((order) => order._id === updatedOrder._id);
    Object.assign(order, updatedOrder);
  },
  SET_ORDER_COUNT: (state, orderCount) => (state.orderCount = orderCount),
  UPDATE_ORDER_EXPORT_BUILD_STATE: (state, { orderExportBuildState, orderNumber }) => {
    // Find the order and update its export build state
    const order = state.orders.find((order) => order.number === orderNumber);
    if (order) {
      order.orderExportBuildState = orderExportBuildState;
    }
  },
};

/**
 * Getters
 */
const getters = {
  getOrders: (state) => state.orders,
  getOrder: (state) => (orderId) => state.orders.find((order) => order._id == orderId),
  getOrdersByState: (state) => (orderState) =>
    state.orders.filter((order) => order.orderState == orderState),
  getOrderByNumber: (state) => (orderNumber) =>
    state.orders.find((order) => order.number == orderNumber),
  getOrderCount: (state) => state.orderCount,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
